import React from "react";
import { Link } from "react-router-dom";
import { Mail, ExternalLink } from "lucide-react";

/**
 * Public Support page — hosted at /support so it can be linked from the
 * App Store Connect listing and the iOS app. Apple requires a support URL
 * on every iOS submission. No authentication required.
 */
const SUPPORT_EMAIL = "wayne@myrepagency.com";

const FAQS = [
  {
    q: "How do I reset my password?",
    a: 'From the iOS app or web sign-in page, tap "Sign in." If you forget your password, contact your rep agency administrator or email us at the address below and we will issue a reset.',
  },
  {
    q: "How do I request access as a new distributor?",
    a: "Tap the access link provided to you by your rep agency. Fill out the registration form with your name, distributor company, location, and contact details. Your rep agency will review and approve your account, then send you a temporary password.",
  },
  {
    q: "Why don't I see all the manufacturers my rep agency carries?",
    a: "Rep agencies often represent different manufacturers in different territories. Your administrator may have limited your account to the lines available in your specific city or branch. Contact your rep agency administrator if you think something is missing.",
  },
  {
    q: "How do I enable Face ID / Touch ID sign-in?",
    a: 'After your first password sign-in on the iOS app, you will be prompted to enable Face ID or Touch ID. You can also enable or disable it any time from the Profile screen.',
  },
  {
    q: "How do I invite a coworker at my distributor?",
    a: 'On the Search screen, tap "Invite a coworker." Enter their email and we will send your rep agency a request to add them. The agency will review and approve.',
  },
  {
    q: "I can't find the product I'm looking for.",
    a: "Type your model number, application, or specs into the chat (e.g., \"2x4 4000K 40W\" or \"AK80LS-1\"). If the wizard cannot find a match, it will surface a contact form so your rep agency can help you directly.",
  },
  {
    q: "How do I delete my account?",
    a: "Email us at the address below from the email on file and request account deletion. We will remove your account within 30 days.",
  },
];

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/login"
            className="inline-flex items-center gap-3"
            data-testid="support-logo-link"
          >
            <img
              src="/byrd-logo.png"
              alt="My Rep Agency"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/login"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-900 font-mono-data"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Support
        </div>
        <h1 className="mt-1 text-4xl font-display font-bold tracking-tight text-slate-900">
          We're here to help.
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-2xl">
          Questions about your account, the product search, or anything else?
          Reach out and we will get back to you, usually within one business
          day.
        </p>

        {/* Primary contact card */}
        <section
          className="mt-8 bg-slate-900 text-white rounded-sm p-6"
          data-testid="support-contact-card"
        >
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">
            Contact
          </div>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-sm bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-data">
                  Email support
                </div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=My%20Rep%20Agency%20Support`}
                  className="text-lg font-display font-semibold tracking-tight hover:text-amber-400 break-all"
                  data-testid="support-email-link"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=My%20Rep%20Agency%20Support`}
              className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors whitespace-nowrap"
              data-testid="support-email-button"
            >
              Send a message
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* FAQs */}
        <section className="mt-12">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Frequently asked
          </div>
          <h2 className="mt-1 text-2xl font-display font-bold tracking-tight text-slate-900">
            Quick answers
          </h2>

          <div className="mt-6 divide-y divide-border border-y border-border">
            {FAQS.map((item, idx) => (
              <details
                key={idx}
                className="group py-4"
                data-testid={`support-faq-${idx}`}
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-medium text-slate-900">{item.q}</span>
                  <span className="text-amber-600 text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed pr-6">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Hours / SLA */}
        <section
          className="mt-12 bg-slate-50 border border-border rounded-sm p-6"
          data-testid="support-hours"
        >
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Response time
          </div>
          <p className="mt-2 text-sm text-slate-800">
            We respond to support emails Monday through Friday, 8:00 AM – 5:00 PM
            Central Time. Most requests are answered within one business day.
            For urgent quote-pricing questions, please also copy your local
            My Rep Agency contact.
          </p>
        </section>

        <div className="mt-16 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono-data">
          <span>© {new Date().getFullYear()} Byrd Rep Agency, LLC</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-slate-900">
              Privacy
            </Link>
            <Link to="/login" className="hover:text-slate-900">
              Back to sign in →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
